export enum CreateTokenOutcome {
  SUCCESS = 'SUCCESS',
  FAILURE_CANNOT_LOGIN = 'FAILURE_CANNOT_LOGIN',
  FAILURE_WRONG_CREDENTIALS = 'FAILURE_WRONG_CREDENTIALS',
  /**
   * BRN-2208 L5 finding F1 — brain-iam's `CreateTokenOutcome.FAILURE_ACCOUNT_NOT_PROVISIONED`.
   * Auth0 VERIFIED the credential and brain-iam then refused to mint a session because the account
   * is not usable (no linked identity row). Still a 401, and still a refusal — but it is NOT a
   * credential failure, and rendering it as one is what sent this population to a password reset
   * that completes successfully and cannot fix anything.
   */
  FAILURE_ACCOUNT_NOT_PROVISIONED = 'FAILURE_ACCOUNT_NOT_PROVISIONED',
  /**
   * BRN-2208 L6 finding L4-R — brain-iam's `CreateTokenOutcome.FAILURE_WRONG_USER_STATUS`, emitted
   * by `AuthServiceImpl.admissionFailureFor` for an ARCHIVED user ('User archived') or a BLOCKED one
   * ('User blocked'). The credential was fine; the account is not admissible.
   *
   * Until this member existed, `outcome === CreateTokenOutcome.<...>` matched nothing here and the
   * refusal fell through to "The user name or password is incorrect" — the same live dead end L5
   * finding F1 fixed one outcome over, with the same useless remedy: a password reset that completes,
   * sends a real e-mail, and cannot un-archive anybody.
   *
   * SCOPE, because it is easy to over-read this fix: brain-iam returns this outcome on the PASSWORD
   * route only. Its MFA-completion route computes the same admission failure and DISCARDS it in
   * favour of `FAILURE_WRONG_CREDENTIALS`, so an archived or blocked user completing an MFA challenge
   * still reaches the credential banner by a second, server-side mechanism this member cannot reach.
   * Recorded in `brain-iam/contracts/login-outcome-contract.json` under `emission_surfaces`.
   */
  FAILURE_WRONG_USER_STATUS = 'FAILURE_WRONG_USER_STATUS',
  MFA_REQUIRED = 'MFA_REQUIRED',
}

export interface TokenDTO {
  outcome: CreateTokenOutcome;
  accessToken?: string;
  idToken?: string;
  refreshToken?: string;
  mfaToken?: string;
  oobCode?: string;
  message?: string;
  bindingCode?: string;
}

export interface TokensRequest {
  accessToken: string;
  idToken: string;
}

export interface TokensResponse {
  id: string;
  accessToken: string;
  idToken: string;
}

/** BRN-2148: `POST /api/public/v1/tokens/sso-redemption` response. This is the actual wire shape —
 *  brain-iam's `SsoTokenRedemptionResponseDto` serializes via explicit `@JsonProperty` to
 *  `access_token`/`refresh_token`/`token_type`/`expires_in` (snake_case), matching the shape
 *  brain-agents' and brain-estream's own `SsoRedemptionResponse` types already declare. Do NOT
 *  camelCase these fields: `HttpClient.post<T>()` performs no key transformation, so a camelCase
 *  declaration here type-checks but silently reads `undefined` off every field at runtime (BRN-2148
 *  code-review B14). Note there is deliberately no `id_token` field on the wire (the underlying
 *  handoff row stores an identical value for both) — the caller must treat `access_token` as
 *  serving both roles, mirroring `SsoCallbackService.mintHandoff`'s own internal modeling. See
 *  implementation-notes-shard-D2.md for the cross-shard flag. */
export interface SsoRedemptionResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}
